import React, { useState, useEffect, lazy, Suspense } from "react";
import { lessonCount } from "../../utils/lessonCount";
import {
  AppBar,
  Container,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  createGenerateClassName,
  StylesProvider,
  Box,
  withStyles,
  Popper,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  ClickAwayListener,
  createTheme,
  ThemeProvider,
  useMediaQuery,
} from "@material-ui/core";

// Custom ClickAwayListener wrapper that filters out unsupported props
const SafeClickAwayListener = ({ children, onClickAway, ...otherProps }) => {
  // Filter out any props that ClickAwayListener doesn't support
  const { style, className, ...safeProps } = otherProps;
  return (
    <ClickAwayListener onClickAway={onClickAway} {...safeProps}>
      {children}
    </ClickAwayListener>
  );
};

import MenuIcon from "@material-ui/icons/Menu";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import CalendarTodayOutlinedIcon from "@material-ui/icons/CalendarTodayOutlined";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import VerticalSplitOutlinedIcon from "@material-ui/icons/VerticalSplitOutlined";
import SwyvelAI from "../SwyvelAI";
import SideNav from "../SideNav";
import OnSwitch from "../OnSwitch";
import {
  SIDE_NAV_MIN_WIDTH,
  applySideNav,
  canUseSideNav,
  choseSideNav,
} from "../../utils/mainNav";
// Lazy: SignIn drags libphonenumber (~137KB) and the whole auth form into
// every page's boot cost, but only logged-out visitors ever open it. The
// chunk is warmed during browser idle time (below) so the modal still
// opens instantly on the landing page — the signup funnel must never wait.
const SignIn = lazy(() => import("../Modal/SignIn"));
import logo from "../../assets/images/logo.svg";
import notification from "../../assets/images/notification.svg";
import staffInbox from "../../assets/images/staff_inbox.svg";
import calendar from "../../assets/images/calendar.svg";
import check from "../../assets/images/check.svg";
import usersApi from "../../apis/usersApi";
import {
  NOTIFICATION_EVENT_TYPES,
  NOTIFICATION_EVENT_TYPES_CAN_NOT_VIEW,
  NOTIFICATION_EVENT_TYPES_CAN_VIEW,
  NOTIFICATION_STATUS,
  USER_ROLES,
} from "../../utils/constants";
import { Coin } from "../LessonCoin";
import useLessonCoins from "../LessonCoin/useLessonCoins";
import notificationsApi from "../../apis/notificationsApi";
import staffMessagesApi from "../../apis/staffMessagesApi";
import BlockIcon from "@material-ui/icons/Block";
import LockIcon from "@material-ui/icons/Lock";
import { formatInTimeZone } from "../../utils/dateTime";
import { isBrowser, isMobile } from "../../utils/common";
// Lazy for the same reason: CalendarSync pulls lodash into every page.
const CalendarSyncModal = lazy(() => import("../Modal/CalendarSync"));

const generateClassName = createGenerateClassName({
  productionPrefix: "c",
  disableGlobal: true,
  seed: "header",
});

const theme = createTheme({
  typography: {
    fontFamily: "'Nunito', 'Varela Round', sans-serif",
  },
  overrides: {
    MuiFormLabel: {
      root: {
        "&$focused": {
          color: "#333333",
        },
      },

    },
    MuiInput: {
      underline: {
        "&::after": {
          borderBottom: "2px solid #333333",
        },
      },
    },
  },
});

const HeaderAppBar = withStyles(() => ({
  root: {
    boxShadow: "none !important",
    backgroundColor: "#ffffff",
    padding: "8px 0",
  },
}))(AppBar);

const Nav = withStyles((theme) => ({
  root: {
    marginLeft: "auto",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const NavMenu = withStyles(() => ({
  root: {
    display: "flex",
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const NavMenuItem = withStyles(() => ({
  root: {
    padding: "0 24px",
    cursor: "pointer",
    color: "#666666",
    display: "flex",
    // The wordmark sets a 52px row; anything shorter (the Swyvel AI pill)
    // sat on the row's top edge, 8px above the icons beside it.
    alignItems: "center",
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const UserSettingsPopper = withStyles(() => ({
  root: {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    border: "1px solid #E0E0E0",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)",
    width: 240,
    position: "relative",
    left: 16,
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const NotificationPopper = withStyles((theme) => ({
  root: {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    border: "1px solid #E0E0E0",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)",
    position: "relative",
    padding: "16px 0",
    width: 300,
    zIndex: 2,
    "& li": {
      "&:hover": {
        backgroundColor: "#F7F8F9",
        cursor: "pointer",
      },
      "&:last-child": {
        borderBottom: "1px solid #E4E4E4",
      },
    },
    [theme.breakpoints.down("sm")]: {
      top: "10px",
    },
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const IconMenuMobile = withStyles((theme) => ({
  root: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
}))(IconButton);

const IconUser = withStyles(() => ({
  root: {
    marginRight: 8,
    fontSize: 50,
  },
}))(AccountCircleIcon);

const UserSettingsPopperTriangle = withStyles(() => ({
  root: {
    position: "absolute",
    top: "-7px",
    left: "auto",
    right: "10%",
    width: 12,
    height: 12,
    margin: "auto",
    transform: "rotate(45deg)",
    "-webkit-transform": "rotate(225deg)",
    "-moz-transform": "rotate(45deg)",
    "-o-transform": "rotate(45deg)",
    "-ms-transform": "rotate(45deg)",
    borderRight: "1px solid #E0E0E0",
    borderBottom: "1px solid #E0E0E0",
    backgroundColor: "#ffffff",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)",
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const NotificationPopperTriangle = withStyles((theme) => ({
  root: {
    position: "absolute",
    top: "-7px",
    left: "50%",
    width: 12,
    height: 12,
    margin: "auto",
    transform: "translateX(-6px) rotate(45deg)",
    "-webkit-transform": "translateX(-6px) rotate(225deg)",
    "-moz-transform": "translateX(-6px) rotate(45deg)",
    "-o-transform": "translateX(-6px) rotate(45deg)",
    "-ms-transform": "translateX(-6px) rotate(45deg)",
    borderRight: "1px solid #E0E0E0",
    borderBottom: "1px solid #E0E0E0",
    backgroundColor: "#ffffff",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)",
    [theme.breakpoints.down("sm")]: {
      left: "59%",
    },
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const UserAvatar = withStyles(() => ({
  root: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    marginRight: 10,
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const StudentTokenHeader = withStyles(() => ({
  root: {},
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const StudentName = withStyles(() => ({
  root: {},
}))(Typography);

const StudentToken = withStyles(() => ({
  root: {
    display: "flex",
    alignItems: "center",
  },
}))(Typography);

const UsedToken = withStyles(() => ({
  root: {
    color: "#1a1a1a",
    fontWeight: 700,
    fontSize: 14.5,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
}))(Typography);

const NotificationWrapper = withStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      marginRight: 50,
    },
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const NotificationBadge = withStyles((theme) => ({
  root: {
    top: "7px",
    right: "-4px",
    width: 10,
    height: 10,
    padding: 4,
    position: "absolute",
    fontSize: "12px",
    color: "#FFFFFF",
    borderRadius: "50%",
    backgroundColor: "#FF0000",
    border: "1px solid #FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      top: "-5px",
    },
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const NotificationPrimaryText = withStyles(() => ({
  root: {
    color: "#1A1A1A",
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "24px",
    padding: "0 16px",
  },
}))(Typography);

const NotificationSecondaryText = withStyles(() => ({
  root: {
    color: "#5E5E5E",
    fontSize: 14,
    fontWeight: "normal",
    lineHeight: "22px",
    padding: "0 16px",
  },
}))(Typography);

const MobileMenu = withStyles(() => ({
  root: {
    alignItems: "center",
    flexDirection: "row",
    display: "flex",
  },
}))(React.forwardRef(({ style, ...props }, ref) => <Box ref={ref} {...props} />));

const ListNotification = withStyles(() => ({
  root: {
    maxHeight: 280,
    overflow: "auto",
  },
}))(List);

const ListItemNoti = withStyles(() => ({
  root: {
    backgroundColor: ({ unread }) =>
      unread === "true" ? "#F7F8F9" : "#ffffff",
  },
}))(ListItem);

const Header = () => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [isShowSignInModal, setIsShowSignInModal] = useState(false);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
  const [popperEl, setPopperEl] = useState(null);
  const [openPopper, setOpenPopper] = useState(false);
  const [notificationEl, setNotificationEl] = useState(null);
  const [openNotification, setOpenNotification] = useState(false);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const currentUser = window.current_user || {};
  const [isOpenCalendarSyncModal, setIsOpenCalendarSyncModal] = useState(false);

  // isMobile is a hook: called once, here, so the side menu standing in for
  // the top bar (which used to call it) never changes the hook count.
  const onMobile = isMobile();

  // The menu down the left side, for an owner or teacher who chose it (the
  // switch in the account menu). On a screen narrower than a small laptop
  // the top menu stays, whatever they chose.
  const sideAllowed = !!currentUser.id && canUseSideNav(currentUser);
  const [sideChosen, setSideChosen] = useState(() => sideAllowed && choseSideNav(currentUser));
  const [sideEntering, setSideEntering] = useState(false);
  const wideEnough = useMediaQuery(`(min-width: ${SIDE_NAV_MIN_WIDTH}px)`, { noSsr: true });
  const sideNav = sideAllowed && sideChosen && wideEnough;
  // The server drew the page from a cookie; the setting is the truth.
  useEffect(() => {
    applySideNav(sideAllowed && sideChosen);
  }, [sideAllowed, sideChosen]);
  const [sideSaving, setSideSaving] = useState(false);
  const toggleSideNav = async () => {
    if (sideSaving) return;
    const next = !sideChosen;
    // The menu it was opened from moves, so it closes; the move is the answer.
    setOpenPopper(false);
    setPopperEl(null);
    setSideEntering(next);
    setSideChosen(next);
    setSideSaving(true);
    try {
      await usersApi.updateNavLayout(next);
      currentUser.user_setting = { ...(currentUser.user_setting || {}), side_nav: next };
    } catch (error) {
      setSideChosen(!next);
      window.alert("Couldn't move the menu just now. Try again in a moment.");
    } finally {
      setSideSaving(false);
    }
  };

  // Warm the lazy modal chunks once the browser goes idle: they stay out
  // of every page's boot path but are already cached the moment someone
  // clicks Sign In (the funnel) or Calendar Sync.
  useEffect(() => {
    const warm = () => {
      import("../Modal/SignIn");
      import("../Modal/CalendarSync");
    };
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(warm, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(warm, 2500);
    return () => clearTimeout(id);
  }, []);
  const message = window.message || {};
  const { alert } = message || {};
  let email = null;
  let role = null;
  const [isNotiError, setIsNotiError] = useState(false);
  
  if (currentUser.id) {
    email = currentUser.email;
    role = currentUser.role;
  }

  let avatar_url = null;
  if (
    currentUser.id &&
    currentUser.avatar_url &&
    currentUser.avatar_url !== ""
  ) {
    avatar_url = currentUser.avatar_url;
  }

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const signOut = async () => {
    try {
      localStorage.removeItem("last_notification_id");
      // setOpenPopper(!openPopper);
      await usersApi.signOut();
    } catch (error) {
      console.error("Sign out error:", error);
      // Continue with redirect even if API call fails
    } finally {
      window.location = "/";
    }
  };

  const mobileMenuId = "mobile-menu";

  const menuItemLinkStyle = {
    fontSize: 16,
    color: "#666666",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    fontWeight: 500,
  };

  const onOpenPopper = (event) => {
    setPopperEl(event.currentTarget);
    setOpenPopper(!openPopper);
  };

  const goToProfilePage = () => {
    // if (!isBrowser()) {
    //   setMobileMoreAnchorEl(null);
    // } else {
    //   setOpenPopper(!openPopper);
    // }
    if (currentUser.id) {
      window.location = `/users/${currentUser.id}`;
    }
  };

  const goToMyLessonsPage = () => {
    window.location = "/lessons";
  };

  const goToProductsPage = () => {
    // if (isMobile()) {
    //   setMobileMoreAnchorEl(null);
    // } else {
    //   setOpenPopper(!openPopper);
    // }
    window.location = "/products";
  };

  const goToFeaturesPage = () => {
    window.location = "/features";
  }

  const goToPricingPage = () => {
    window.location = "/pricing";
  }

  const goToAiPage = () => {
    window.location = "/ai";
  }

  const onCloseNotification = (event) => {
    setNotificationEl(null);
    setOpenNotification(false);
  };

  const onClosePopper = (event) => {
    setPopperEl(null);
    setOpenPopper(false);
  };

  const [notifications, setNotifications] = useState([]);
  const [notificationCountBell, setNotificationCountBell] = useState();
  // Staff inbox unread count — owners and teachers only; a fetch failure
  // just means no badge.
  const [staffUnreadCount, setStaffUnreadCount] = useState(0);
  useEffect(() => {
    if (role !== USER_ROLES.STUDIO && role !== USER_ROLES.INSTRUCTOR) return;
    staffMessagesApi
      .getInbox()
      .then((res) => setStaffUnreadCount(res?.unread_count || 0))
      .catch(() => {});
  }, []);
  // A student's lessons still to take, as coins beside their name.
  const lessonCoins = useLessonCoins(currentUser);
  const coinsLabel = lessonCoins.coins.length > 1
    ? `Lessons left: ${lessonCoins.coins.map((c) => `${c.left} ${c.name}`).join(", ")}`
    : `${lessonCount(lessonCoins.left)} lesson${lessonCoins.left === 1 ? "" : "s"} left`;
  const coinsLine = `${lessonCount(lessonCoins.left)} lesson${lessonCoins.left === 1 ? "" : "s"} to take`;
  const keptLine = `${lessonCoins.collected} taken and kept`;
  const [isSeeAllNotifications, setIsSeeAllNotifications] = useState(false);

  const onOpenNotification = (event) => {
    event.preventDefault();
    if (notifications.length > 0) {
      localStorage.setItem("last_notification_id", notifications[0].id);
    }

    setNotificationEl(event.currentTarget);
    setOpenNotification(!openNotification);
    setIsSeeAllNotifications(true);
  };

  const fetchNotificationListFirstLoad = async () => {
    try {
      const isUserLogoutStorage = localStorage.getItem("isUserLogout");

      if (
        !isNotiError &&
        (!isUserLogoutStorage ||
          (isUserLogoutStorage && isUserLogoutStorage === "false"))
      ) {
        localStorage.removeItem("isUserLogout");
        const res = await notificationsApi.getList();
        if (Array.isArray(res) && res.length > 0) {
          setNotifications(res);

          const lastNotificationId = localStorage.getItem(
            "last_notification_id"
          );

          let notificationsUnReadNew = [];

          if (lastNotificationId) {
            const indexLatestNotification = res.findIndex(
              (n) => n.id === lastNotificationId
            );

            if (indexLatestNotification !== -1) {
              notificationsUnReadNew = res.filter((noti, index) => {
                return (
                  noti.attributes.status === NOTIFICATION_STATUS.UNREAD &&
                  index < indexLatestNotification
                );
              });
            }
          } else {
            notificationsUnReadNew = res.filter((noti) => {
              return noti.attributes.status === NOTIFICATION_STATUS.UNREAD;
            });
          }

          if (notificationsUnReadNew.length > 0) {
            setNotificationCountBell(notificationsUnReadNew.length);
          }
        }
        setIsNotiError(false);
      }
    } catch (error) {
      setIsNotiError(true);
    }
  };

  const fetchNotificationList = async () => {
    try {
      const isUserLogoutStorage = localStorage.getItem("isUserLogout");
      if (
        !isNotiError &&
        (!isUserLogoutStorage ||
          (isUserLogoutStorage && isUserLogoutStorage === "false"))
      ) {
        localStorage.removeItem("isUserLogout");
        const res = await notificationsApi.getList();
        if (Array.isArray(res) && res.length > 0) {
          setNotifications(res);

          const lastNotificationId = localStorage.getItem(
            "last_notification_id"
          );

          let notificationsUnReadNew = [];

          if (lastNotificationId) {
            const indexLatestNotification = res.findIndex(
              (n) => n.id === lastNotificationId
            );

            if (indexLatestNotification !== -1) {
              notificationsUnReadNew = res.filter((noti, index) => {
                return (
                  noti.attributes.status === NOTIFICATION_STATUS.UNREAD &&
                  index < indexLatestNotification
                );
              });
            }
          } else {
            notificationsUnReadNew = res.filter((noti) => {
              return noti.attributes.status === NOTIFICATION_STATUS.UNREAD;
            });
          }

          if (notificationsUnReadNew.length > 0) {
            setNotificationCountBell(notificationsUnReadNew.length);

            setIsSeeAllNotifications(false);
          }
        }
        setIsNotiError(false);
      }
    } catch (error) {
      setIsNotiError(true);
    }
  };

  const readNotificationWithIds = async (ids) => {
    try {
      await notificationsApi.update({
        notifications: {
          ids,
        },
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (!isNotiError && currentUser.id && !alert) {
      fetchNotificationListFirstLoad();
    }
  }, [alert, isNotiError]);

  useEffect(() => {
    let interval = null;
    if (!isNotiError && currentUser.id && !alert) {
      interval = setInterval(() => {
        fetchNotificationList();
      }, 50000);
    }

    if (interval) return () => clearInterval(interval);
  }, [alert, isNotiError, currentUser.id]);

  const onClickNotification = async (notification) => {
    const { id, attributes } = notification;
    const { event_type, status, event_url } = attributes;

    if (status === NOTIFICATION_STATUS.UNREAD) {
      setNotifications(
        notifications.map((noti) => {
          if (noti.id === id) {
            return {
              ...noti,
              attributes: {
                ...noti.attributes,
                status: NOTIFICATION_STATUS.READ,
              },
            };
          }
          return noti;
        })
      );

      await readNotificationWithIds([id]);
    }

    if (NOTIFICATION_EVENT_TYPES_CAN_VIEW.includes(event_type)) {
      if (event_type === NOTIFICATION_EVENT_TYPES.PROPOSAL_CREATED) {
        const proposedTimeId = event_url.split("lessons/")[1];
        const url = `/proposed_times?open=${proposedTimeId}`;
        window.location.href = url;
      } else {
        window.location.href = event_url;
      }
    }

    setNotificationEl(null);
    setOpenNotification(false);
  };

  const renderEventType = (eventType) => {
    switch (eventType) {
      case NOTIFICATION_EVENT_TYPES.PROPOSAL_CREATED:
        return <img src={calendar} />;
      case NOTIFICATION_EVENT_TYPES.MEMBERSHIP_REJECTED:
      case NOTIFICATION_EVENT_TYPES.EMPLOYMENT_REJECTED:
      case NOTIFICATION_EVENT_TYPES.LESSON_CANCELLED:
        return <BlockIcon style={{ color: "#FF0000" }} />;

      default:
        return <img src={check} />;
    }
  };

  const userLogin = () => {
    window.location = "/users/login"
  }

  return (
    <>
      <StylesProvider generateClassName={generateClassName}>
        <ThemeProvider theme={theme}>
          {sideNav ? (
            <SideNav
              entering={sideEntering}
              avatarUrl={avatar_url}
              staffUnreadCount={staffUnreadCount}
              notificationCount={notificationCountBell && !isSeeAllNotifications ? notificationCountBell : 0}
              accountOpen={openPopper}
              onOpenAccount={onOpenPopper}
              onOpenNotifications={onOpenNotification}
            />
          ) : (
          <Box
            style={{
              // The bar is white edge to edge; only its contents are
              // constrained. On the grey ground a white panel the width of
              // the content read as a floating card, not a header.
              background: "#ffffff",
              borderBottom: "1px solid #E1E5EA",
            }}
          >
            <Container>
              <HeaderAppBar position="static" color={"default"}>
                <Toolbar style={{ justifyContent: "space-between" }}>
                  <a href="/" style={{ textDecoration: "none" }}>
                    <h1
                      style={{
                        fontSize: 36,
                        fontWeight: 400,
                        lineHeight: "52px",
                        color: "#000000",
                        textTransform: "uppercase",
                        margin: 0,
                      }}
                    >
                      Swyvel
                    </h1>
                  </a>
                  <Nav>
                    {currentUser.id ? (
                      <NavMenu>
                        <NavMenuItem>
                          <SwyvelAI />
                        </NavMenuItem>
                        {(role === USER_ROLES.STUDIO || role === USER_ROLES.INSTRUCTOR) && (
                          <NavMenuItem>
                            <NotificationWrapper
                              onClick={() => (window.location.href = "/inbox")}
                              title="Staff inbox"
                            >
                              <img src={staffInbox} alt="Staff inbox" />
                              {staffUnreadCount > 0 && (
                                <NotificationBadge>{staffUnreadCount}</NotificationBadge>
                              )}
                            </NotificationWrapper>
                          </NavMenuItem>
                        )}
                        <NavMenuItem>
                          <NotificationWrapper onClick={onOpenNotification}>
                            <img src={notification} alt="" />
                            {notificationCountBell &&
                              !isSeeAllNotifications && (
                                <NotificationBadge>
                                  {notificationCountBell}
                                </NotificationBadge>
                              )}
                          </NotificationWrapper>
                        </NavMenuItem>
                        <NavMenuItem>
                          <a
                            style={{
                              ...menuItemLinkStyle,
                              color: "#000000",
                            }}
                            onClick={onOpenPopper}
                            aria-label="Account menu"
                          >
                            {avatar_url ? (
                              <UserAvatar>
                                <img
                                  src={avatar_url}
                                  alt=""
                                  style={{
                                    width: "100%",
                                    borderRadius: "50%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </UserAvatar>
                            ) : (
                              <IconUser />
                            )}
                            {currentUser.id && (
                              <>
                                {currentUser.role === USER_ROLES.STUDENT ? (
                                  <StudentTokenHeader>
                                    <StudentName component="span">
                                      {`${currentUser.first_name}`}
                                    </StudentName>
                                    {lessonCoins.coins.length > 0 && (
                                      <StudentToken component="div" data-testid="lesson-coins" aria-label={coinsLabel} title={coinsLabel}>
                                        {lessonCoins.coins.map((c) => (
                                          <span key={lessonCoins.idOf(c)} style={{ display: "inline-flex", alignItems: "center", gap: 5, marginRight: 9 }}>
                                            <Coin color={c.color} size={16} className={lessonCoins.changed[lessonCoins.idOf(c)] ? "juice-coin-flip" : undefined} />
                                            <UsedToken component="span">{lessonCount(c.left)}</UsedToken>
                                          </span>
                                        ))}
                                      </StudentToken>
                                    )}
                                  </StudentTokenHeader>
                                ) : (
                                  <span>{`${currentUser.first_name}`}</span>
                                )}
                              </>
                            )}
                            <ExpandMoreIcon style={{ marginLeft: 8 }} />
                          </a>
                        </NavMenuItem>
                      </NavMenu>
                    ) : (
                      <NavMenu>
                        <NavMenuItem>
                          <a
                            onClick={goToAiPage}
                            style={{ ...menuItemLinkStyle, display: "inline-flex", alignItems: "center", gap: 6 }}
                          >
                            AI
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#fff",
                                background: "linear-gradient(135deg, #2a1c4d 0%, #1b2547 100%)",
                                borderRadius: 140,
                                padding: "2px 7px",
                              }}
                            >
                              New
                            </span>
                          </a>
                        </NavMenuItem>

                        <NavMenuItem>
                          <a
                            onClick={goToFeaturesPage}
                            style={menuItemLinkStyle}
                          >
                            Features
                          </a>
                        </NavMenuItem>

                        <NavMenuItem>
                          <a
                            onClick={goToPricingPage}
                            style={menuItemLinkStyle}
                          >
                            Pricing
                          </a>
                        </NavMenuItem>

                        <NavMenuItem>
                          <a
                            onClick={userLogin}
                            style={menuItemLinkStyle}
                          >
                            Sign in/Sign up
                          </a>
                        </NavMenuItem>
                      </NavMenu>
                    )}
                  </Nav>
                  {onMobile && (
                    <MobileMenu>
                      {currentUser.id && (
                        <NotificationWrapper onClick={onOpenNotification}>
                          <img src={notification} alt="" />
                          {notificationCountBell && !isSeeAllNotifications && (
                            <NotificationBadge>
                              {notificationCountBell}
                            </NotificationBadge>
                          )}
                        </NotificationWrapper>
                      )}

                      <IconMenuMobile
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleMobileMenuOpen}
                        aria-controls={mobileMenuId}
                        aria-haspopup="true"
                      >
                        <MenuIcon />
                      </IconMenuMobile>
                    </MobileMenu>
                  )}
                </Toolbar>
              </HeaderAppBar>
            </Container>
          </Box>
          )}

          {/* Stripe couldn't collect the studio's Swyvel renewal. The studio
              stays open while it retries; this is the one place the owner
              sees it in the app, with the fix one click away. */}
          {role === USER_ROLES.STUDIO && currentUser.studio_renewal_due && (
            <Box
              data-testid="renewal-banner"
              style={{
                background: "#FFFBEB",
                borderBottom: "1px solid #FDE68A",
                textAlign: "center",
                padding: "7px 16px",
                fontSize: 13.5,
                fontFamily: "'Nunito', 'Varela Round', sans-serif",
                color: "#1a1a1a",
              }}
            >
              Your last Swyvel payment didn't go through.
              {currentUser.studio_renewal_due.open_until
                ? ` Your studio stays open until ${currentUser.studio_renewal_due.open_until}.`
                : ""}{" "}
              <a
                href="/subscription/pay"
                data-no-prefetch
                style={{ color: "#B45309", fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}
              >
                Pay the invoice
              </a>
            </Box>
          )}

          {/* Persistent trial countdown for owners — previously trial status
              was only visible on the Overview tab of My Business. */}
          {role === USER_ROLES.STUDIO &&
            typeof currentUser.studio_trial_days_remaining === "number" && (
              <Box
                style={{
                  background:
                    currentUser.studio_trial_days_remaining <= 3 ? "#FFFBEB" : "#F4F8FF",
                  borderBottom: `1px solid ${
                    currentUser.studio_trial_days_remaining <= 3 ? "#FDE68A" : "#DFEBFF"
                  }`,
                  textAlign: "center",
                  padding: "6px 16px",
                  fontSize: 13,
                  fontFamily: "'Nunito', 'Varela Round', sans-serif",
                  color: "#1a1a1a",
                }}
              >
                {currentUser.studio_trial_days_remaining}{" "}
                {currentUser.studio_trial_days_remaining === 1 ? "day" : "days"} left in your
                free trial{" · "}
                {/* The offer, not just the toll booth: founding pricing is the
                    real price-lock deal the paywall makes. "from" because $39
                    is the annual rate (monthly is $49). */}
                Founding pricing from $39/mo, locked for good{" · "}
                <a
                  href="/paywall"
                  style={{ color: "#007BFF", fontWeight: 600, textDecoration: "none" }}
                >
                  Upgrade now
                </a>
              </Box>
            )}

          {isShowSignInModal && (
            <Suspense fallback={null}>
              <SignIn
                isOpen={isShowSignInModal}
                onClose={() => setIsShowSignInModal(false)}
              />
            </Suspense>
          )}

          {isOpenCalendarSyncModal && (
            <Suspense fallback={null}>
              <CalendarSyncModal
                isOpen={isOpenCalendarSyncModal}
                onClose={() => setIsOpenCalendarSyncModal(false)}
                currentUser={currentUser}
                email={email}
              />
            </Suspense>
          )}

          {onMobile && (
            <>
              {currentUser.id ? (
                <Menu
                  anchorEl={mobileMoreAnchorEl}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  id={mobileMenuId}
                  keepMounted
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  open={isMobileMenuOpen}
                  onClose={handleMobileMenuClose}
                >
                  {currentUser.id && currentUser.role === USER_ROLES.STUDENT && (lessonCoins.coins.length > 0 || lessonCoins.collected > 0) && (
                    <MenuItem onClick={goToMyLessonsPage} data-testid="lesson-coins-menu">
                      <ListItemIcon
                        style={{
                          minWidth: 35,
                        }}
                      >
                        <Coin color={(lessonCoins.coins[0] || {}).color} size={20} />
                      </ListItemIcon>
                      <ListItemText primary={coinsLine} secondary={keptLine} />
                    </MenuItem>
                  )}

                  <MenuItem onClick={goToProfilePage}>
                    <ListItemIcon
                      style={{
                        minWidth: 35,
                      }}
                    >
                      <PersonOutlineIcon />
                    </ListItemIcon>
                    <ListItemText primary="Profile" />
                  </MenuItem>
                  {/* Always reachable. This used to be hidden until Google
                      calendar access had been granted, which made it a door
                      that only opened from the inside: anyone who declined the
                      scope had no way back to it. The modal itself now handles
                      the not-yet-connected case. */}
                  <MenuItem
                    onClick={() => {
                      setIsOpenCalendarSyncModal(true);
                      handleMobileMenuClose();
                    }}
                  >
                    <ListItemIcon
                      style={{
                        minWidth: 35,
                      }}
                    >
                      <CalendarTodayOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary="Calendar Sync" />
                  </MenuItem>
                 
                  {role === USER_ROLES.STUDENT && currentUser.buys_on_packages_page && (
                      <MenuItem onClick={goToProductsPage}>
                        <ListItemIcon
                          style={{
                            minWidth: 35,
                          }}
                        >
                          <ReceiptOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="My Packages" />
                      </MenuItem>
                    )}
                  {role === USER_ROLES.STUDIO && (
                    <MenuItem
                      onClick={() => {
                        handleMobileMenuClose();
                        window.location.href = "/?setup_guide=1";
                      }}
                    >
                      <ListItemIcon style={{ minWidth: 35 }}>
                        <PlaylistAddCheckIcon />
                      </ListItemIcon>
                      <ListItemText primary="Setup Guide" />
                    </MenuItem>
                  )}
                  {role === USER_ROLES.STUDIO && (
                    <MenuItem
                      onClick={() => {
                        handleMobileMenuClose();
                        window.location.href = "/subscription";
                      }}
                    >
                      <ListItemIcon style={{ minWidth: 35 }}>
                        <ReceiptOutlinedIcon />
                      </ListItemIcon>
                      <ListItemText primary="Billing & plan" />
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      handleMobileMenuClose();
                      window.location.href = "/help";
                    }}
                  >
                    <ListItemIcon style={{ minWidth: 35 }}>
                      <HelpOutlineIcon />
                    </ListItemIcon>
                    <ListItemText primary="Help & Guides" />
                  </MenuItem>
                  <MenuItem onClick={signOut}>
                    <ListItemIcon
                      style={{
                        minWidth: 35,
                        color: "#FF0000",
                      }}
                    >
                      <ExitToAppIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Sign out"
                      style={{ color: "#FF0000" }}
                    />
                  </MenuItem>
                </Menu>
              ) : (
                <Menu
                  anchorEl={mobileMoreAnchorEl}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  id={mobileMenuId}
                  keepMounted
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  open={isMobileMenuOpen}
                  onClose={handleMobileMenuClose}
                >
                  <MenuItem
                    onClick={userLogin}
                  >
                    Sign in/Sign up
                  </MenuItem>
                  <MenuItem
                    onClick={goToFeaturesPage}
                  >
                    Features
                  </MenuItem>
                  <MenuItem
                    onClick={goToPricingPage}
                  >
                    Pricing
                  </MenuItem>
                  <MenuItem onClick={goToAiPage}>
                    AI
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#fff",
                        background: "linear-gradient(135deg, #2a1c4d 0%, #1b2547 100%)",
                        borderRadius: 140,
                        padding: "2px 7px",
                      }}
                    >
                      New
                    </span>
                  </MenuItem>
                </Menu>
              )}
            </>
          )}
          {isBrowser && openPopper && (
            <Popper
              open={openPopper}
              anchorEl={popperEl}
              placement={sideNav ? "right-end" : "bottom-end"}
              transition
              style={{ zIndex: 1300 }}
            >
              {({ TransitionProps }) => {
                const { style, ...filteredTransitionProps } = TransitionProps || {};
                return (
                  <Fade {...filteredTransitionProps} timeout={350}>
                    <SafeClickAwayListener onClickAway={onClosePopper}>
                      <div>
                        <UserSettingsPopper>
                      <List component="nav" aria-label="main mailbox folders">
                        {role === USER_ROLES.STUDENT && (lessonCoins.coins.length > 0 || lessonCoins.collected > 0) && (
                          <ListItem button onClick={goToMyLessonsPage} data-testid="lesson-coins-menu">
                            <ListItemIcon
                              style={{
                                minWidth: 35,
                              }}
                            >
                              <Coin color={(lessonCoins.coins[0] || {}).color} size={20} />
                            </ListItemIcon>
                            <ListItemText primary={coinsLine} secondary={keptLine} />
                          </ListItem>
                        )}
                        <ListItem button onClick={goToProfilePage}>
                          <ListItemIcon
                            style={{
                              minWidth: 35,
                            }}
                          >
                            <PersonOutlineIcon />
                          </ListItemIcon>
                          <ListItemText primary="Profile" />
                        </ListItem>
                        <ListItem
                          button
                          onClick={() => {
                            setIsOpenCalendarSyncModal(true);
                            setOpenPopper(false);
                          }}
                        >
                          <ListItemIcon
                            style={{
                              minWidth: 35,
                            }}
                          >
                            <CalendarTodayOutlinedIcon />
                          </ListItemIcon>
                          <ListItemText primary="Calendar Sync" />
                        </ListItem>
                        {role === USER_ROLES.STUDENT && currentUser.buys_on_packages_page && (
                            <ListItem button onClick={goToProductsPage}>
                              <ListItemIcon
                                style={{
                                  minWidth: 35,
                                }}
                              >
                                <ReceiptOutlinedIcon />
                              </ListItemIcon>
                              <ListItemText primary="My Packages" />
                            </ListItem>
                          )}
                        {role === USER_ROLES.STUDIO && (
                          <ListItem
                            button
                            onClick={() => {
                              setOpenPopper(false);
                              window.location.href = "/?setup_guide=1";
                            }}
                          >
                            <ListItemIcon style={{ minWidth: 35 }}>
                              <PlaylistAddCheckIcon />
                            </ListItemIcon>
                            <ListItemText primary="Setup Guide" />
                          </ListItem>
                        )}
                        {role === USER_ROLES.STUDIO && (
                          <ListItem
                            button
                            onClick={() => {
                              setOpenPopper(false);
                              window.location.href = "/subscription";
                            }}
                          >
                            <ListItemIcon style={{ minWidth: 35 }}>
                              <ReceiptOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText primary="Billing & plan" />
                          </ListItem>
                        )}
                        {/* Where the menu sits, for this person. Offered only
                            where it would show: owners and teachers, on a
                            screen wide enough for it. */}
                        {sideAllowed && wideEnough && (
                          <ListItem button onClick={toggleSideNav} data-testid="side-nav-row">
                            <ListItemIcon style={{ minWidth: 35 }}>
                              <VerticalSplitOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText primary="Menu on the left" />
                            <OnSwitch
                              size="small"
                              checked={sideChosen}
                              disabled={sideSaving}
                              onClick={(event) => event.stopPropagation()}
                              onChange={toggleSideNav}
                              inputProps={{ "aria-label": "Menu on the left", "data-testid": "side-nav-switch" }}
                              style={{ marginRight: -8 }}
                            />
                          </ListItem>
                        )}
                        <ListItem
                          button
                          onClick={() => {
                            setOpenPopper(false);
                            window.location.href = "/help";
                          }}
                        >
                          <ListItemIcon style={{ minWidth: 35 }}>
                            <HelpOutlineIcon />
                          </ListItemIcon>
                          <ListItemText primary="Help & Guides" />
                        </ListItem>
                        <ListItem button onClick={signOut}>
                          <ListItemIcon
                            style={{
                              minWidth: 35,
                              color: "#FF0000",
                            }}
                          >
                            <ExitToAppIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary="Sign out"
                            style={{ color: "#FF0000" }}
                          />
                        </ListItem>
                      </List>
                          {!sideNav && <UserSettingsPopperTriangle />}
                        </UserSettingsPopper>
                      </div>
                    </SafeClickAwayListener>
                  </Fade>
                );
              }}
            </Popper>
          )}

          {openNotification && (
            <Popper
              open={openNotification}
              anchorEl={notificationEl}
              placement={sideNav ? "bottom-start" : "bottom"}
              modifiers={sideNav ? { offset: { offset: "0, 8" } } : undefined}
              transition
              style={{ zIndex: 1300 }}
            >
              {({ TransitionProps }) => {
                const { style, ...filteredTransitionProps } = TransitionProps || {};
                return (
                  <Fade {...filteredTransitionProps} timeout={350}>
                    <SafeClickAwayListener onClickAway={onCloseNotification}>
                      <div>
                        <NotificationPopper>
                      <NotificationPrimaryText>
                        Notifications
                      </NotificationPrimaryText>

                      {notifications.length > 0 && (
                        <NotificationSecondaryText>
                          You have{" "}
                          {
                            notifications.filter(
                              (e) =>
                                e.attributes.status ===
                                NOTIFICATION_STATUS.UNREAD
                            ).length
                          }{" "}
                          unread messages
                        </NotificationSecondaryText>
                      )}

                      <ListNotification>
                        {notifications.map((notification, index) => {
                          const { event_content, send_at, status, event_type } =
                            notification.attributes;

                          return (
                            <ListItemNoti
                              onClick={() => onClickNotification(notification)}
                              key={`notification-${index}`}
                              unread={(
                                status === NOTIFICATION_STATUS.UNREAD
                              ).toString()}
                              style={{
                                cursor:
                                  NOTIFICATION_EVENT_TYPES_CAN_NOT_VIEW.includes(
                                    event_type
                                  )
                                    ? "default"
                                    : "pointer",
                              }}
                            >
                              <ListItemIcon
                                style={{
                                  minWidth: 35,
                                }}
                              >
                                {renderEventType(event_type)}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: event_content
                                    }}
                                  />
                                }
                                secondary={formatInTimeZone(
                                  send_at,
                                  "dd MMM, yyyy",
                                  clientTimeZone
                                )}
                              />
                            </ListItemNoti>
                          );
                        })}
                      </ListNotification>
                          {!sideNav && <NotificationPopperTriangle />}
                        </NotificationPopper>
                      </div>
                    </SafeClickAwayListener>
                  </Fade>
                );
              }}
            </Popper>
          )}
        </ThemeProvider>
      </StylesProvider>
    </>
  );
};

Header.defaultProps = {};

Header.propTypes = {};

export default Header;
